import type { Config as XrayConfig, Xray } from '@mail/xray';
import { xray } from 'lib/xray';
import { EMBEDDED, GA_ID, IS_DEV } from 'reactApp/appHelpers/configHelpers';
import { normalizeSymbols } from 'reactApp/utils/helpers';

const RADAR_PREFIX = IS_DEV ? 'test' : '';

/**
 * Вычисляет максимальную допустимую длину метрики t с учетом p и radarPrefix
 * @param p - проект
 * @param radarPrefix - префикс радара
 * @returns Максимальную допустимую длину метрики t
 */
function getMaxTLenght(p: string, radarPrefix: string = RADAR_PREFIX) {
    const pPart = p ? `${p}_` : '';
    const prefix = radarPrefix ? `${radarPrefix}_` : '';
    return Math.max(0, 64 - `${pPart}${prefix}`.length);
}

/**
 * Вычисляет максимальную допустимую длину метрики i с учетом p, t и radarPrefix
 * @param t - основная метрика
 * @param p - проект
 * @param radarPrefix - префикс радара
 * @returns Максимальную допустимую длину метрики i
 */
function getMaxILenght(t: string, p: string, radarPrefix: string = RADAR_PREFIX) {
    const pPart = p ? `${p}_` : '';
    const prefix = radarPrefix ? `${radarPrefix}_` : '';
    const tPart = t ? `${t}_` : '';
    return Math.min(Math.max(0, 64 - `${pPart}${prefix}${tPart}`.length), 32);
}

function shorten(data, limit) {
    if (!limit || typeof limit !== 'number') {
        return data;
    }

    switch (true) {
        case !limit:
            return data;
        case typeof data === 'string':
            return data.slice(0, limit);
        case typeof data === 'object':
            return Object.keys(data).reduce(
                (previousValue, currentValue) => ({
                    ...previousValue,
                    [currentValue.slice(0, limit)]: data[currentValue],
                }),
                {}
            );
        default:
            return data;
    }
}

export interface IRadarOptions {
    // Принудительная отсылка очереди радара
    sendImmediately?: boolean;
    // Отсылать только ГА, радары не слать
    sendGaOnly?: boolean;
    sendToGa?: boolean;
    dwh?: Record<string, any>;
}

/**
 *
 * @param eventCategory
 * @param action
 * @param label
 * @param iData: {[key: string]: number}. Example: {'ext':1, 'time': 1000}
 * @param options:boolean|IRadarOptions - принудительная отсылка очереди радара
 */
const sendGa = (
    eventCategory,
    action: string | null = '',
    label: string | null = '',
    iData: any = null,
    options: boolean | IRadarOptions = {}
) => {
    const names = [eventCategory];

    if (action) {
        names.push(action);
    }

    if (label) {
        names.push(label);
    }

    if (typeof options === 'boolean') {
        options = {
            sendImmediately: options,
        };
    } else if (!('sendToGa' in options)) {
        options.sendToGa = Boolean(GA_ID);
    }

    sendXray(names, iData, options);
};

/**
 *
 * @param eventCategory
 * @param action
 * @param label
 * @param dwhData: {[key: string]: number}. Example: {'ext':1, 'time': 1000}
 * @param sendImmediately:boolean - принудительная отсылка очереди радара
 */
const sendDwh = ({
    eventCategory,
    action = '',
    label = '',
    sendImmediately = false,
    dwhData = null,
    p,
    dwh_enabled = false,
    dwh_enabled_fields = undefined,
    replacementSymbol = '-',
}: {
    eventCategory?;
    action?;
    label?;
    sendImmediately?;
    dwhData?: any;
    p?: string;
    dwh_enabled?: boolean;
    dwh_enabled_fields?: string[][];
    replacementSymbol?: string;
}) => {
    const i: string[] = [];

    if (action) {
        i.push(`${normalizeSymbols(action, replacementSymbol)}`);
    }

    if (label) {
        i.push(`${normalizeSymbols(label, replacementSymbol)}`);
    }

    const params: any = {
        p: p ? p : EMBEDDED ? 'vk_file_viewer' : 'cloud-web-analytics',
        dwh_enabled,
        dwh_enabled_fields,
    };

    // Сначала пытаемся сократить t как основную метрику
    const name = shorten(normalizeSymbols(eventCategory, replacementSymbol), getMaxTLenght(params.p));

    if (i.length) {
        // Потом сокращаем i как субметрику
        // Сокращаем либо до 32 символов, либо до оставшегося места с учетом p, t и '_'
        params.i = shorten(i.join('_'), getMaxILenght(name, params.p));
    }

    if (dwhData) {
        params.dwh = dwhData;
    }

    // (p + '_' + t + '_' + i.key) <= 64
    // i - длина строки/ключей в объекте не должна превышать 32 символа
    if (sendImmediately) {
        xray.sendImmediately(name, params);
    } else {
        xray.send(name, params);
    }
};

function isExist<T>(item: T | undefined): item is T {
    return !!item;
}

/**
 * @param iData: {[key: string]: number}. Example: {'ext':1, 'time': 1000}
 */
const sendXray = (
    names: string | Array<string | undefined>,
    iData: Record<string, number> | string | null = null,
    options: IRadarOptions = {}
) => {
    let name = '';

    if (Array.isArray(names)) {
        name = names
            .filter(isExist)
            .map((item) => normalizeSymbols(item))
            .join('_');
    } else {
        name = normalizeSymbols(names);
    }

    // Сначала пытаемся сократить t как основную метрику
    name = shorten(name, getMaxTLenght('cloud'));

    const params: Record<string, any> = {
        // Потом сокращаем i как субметрику
        // Сокращаем либо до 32 символов, либо до оставшегося места с учетом p, t и '_'
        i: iData ? shorten(iData, getMaxILenght(name, 'cloud')) : undefined,
    };

    if (options.dwh) {
        params.dwh = options.dwh;
    }

    if (options?.sendGaOnly && options?.sendToGa) {
        // @ts-ignore
        gtag('event', names[1], {
            event_category: names[0],
            event_label: names[2],
        });

        return;
    }

    // (p + '_' + t + '_' + i.key) <= 64
    // i - длина строки/ключей в объекте не должна превышать 32 символа
    if (options.sendImmediately) {
        // @ts-ignore
        xray.sendImmediately(name, params, options.sendToGa);
    } else {
        // @ts-ignore
        xray?.send?.(name, params, options.sendToGa);
    }
};

/**
 *
 * @param eventCategory
 */
const createGaSender =
    (eventCategory) =>
    (action, label: string | null = '', iData: any = null, options: boolean | IRadarOptions = {}) => {
        sendGa(eventCategory, action, label, iData, options);
    };

const createDwhSender =
    (eventCategory) =>
    ({
        action = '',
        label = '',
        sendImmediately = false,
        dwhData = null,
    }: {
        eventCategory?;
        action?;
        label?;
        sendImmediately?: boolean;
        dwhData?: any;
    }) => {
        sendDwh({ eventCategory, action, label, sendImmediately, dwhData });
    };

const sendKaktamLog = (message: Record<string, any>, fileName = 'cloud_common') => {
    xray.send('log', {
        rlog: fileName,
        rlog_message: message,
    });
};

const createXrayInstance = (config: XrayConfig): Xray => {
    const instance = xray.getInstanceCopy();
    instance.setConfig(config);
    return instance;
};

export { createGaSender, sendGa, sendDwh, createDwhSender, sendXray, sendKaktamLog, createXrayInstance, getMaxTLenght, getMaxILenght };
